import React, { useState } from 'react';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  rectSortingStrategy,
  sortableKeyboardCoordinates,
} from '@dnd-kit/sortable';
import WidgetShell from './WidgetShell';

// Sortable dashboard grid. `widgets` = registry entries already filtered
// by role visibility; `renderers` maps widget id -> () => JSX.
export default function DashboardGrid({ widgets, renderers, layout, onMove, onHide, onShow, onReset }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const byId = {};
  widgets.forEach((w) => { byId[w.id] = w; });
  const visibleIds = layout.order.filter((id) => byId[id] && !layout.hidden.includes(id));
  const hiddenWidgets = layout.hidden.map((id) => byId[id]).filter(Boolean);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleDragEnd = ({ active, over }) => {
    if (over && active.id !== over.id) onMove(active.id, over.id);
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12, position: 'relative' }}>
        <button
          type="button"
          onClick={() => setMenuOpen((o) => !o)}
          style={{
            background: 'var(--white)',
            border: '1px solid var(--border-color)',
            borderRadius: 8,
            padding: '6px 14px',
            fontWeight: 600,
            fontSize: 13,
            color: 'var(--text-strong)',
            cursor: 'pointer',
          }}
        >
          ⚙ Customize
        </button>
        {menuOpen && (
          <div
            style={{
              position: 'absolute',
              top: '110%',
              right: 0,
              zIndex: 30,
              background: 'var(--white)',
              border: '1px solid var(--border-color)',
              borderRadius: 10,
              boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
              padding: 12,
              minWidth: 260,
            }}
          >
            <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 8, color: 'var(--text-strong)' }}>
              Hidden widgets
            </div>
            {hiddenWidgets.length === 0 ? (
              <div style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 8 }}>
                No hidden widgets. Use ✕ on a widget to hide it.
              </div>
            ) : (
              hiddenWidgets.map((w) => (
                <div
                  key={w.id}
                  style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, padding: '4px 0' }}
                >
                  <span style={{ fontSize: 13 }}>{w.title}</span>
                  <button
                    type="button"
                    onClick={() => onShow(w.id)}
                    style={{
                      background: 'var(--accent-color)',
                      color: '#fff',
                      border: 'none',
                      borderRadius: 6,
                      padding: '3px 10px',
                      fontSize: 12,
                      fontWeight: 600,
                      cursor: 'pointer',
                    }}
                  >
                    Show
                  </button>
                </div>
              ))
            )}
            <button
              type="button"
              onClick={() => { onReset(); setMenuOpen(false); }}
              style={{
                marginTop: 10,
                width: '100%',
                background: 'var(--surface-muted)',
                border: '1px solid var(--border-color)',
                borderRadius: 6,
                padding: '6px 10px',
                fontSize: 12,
                fontWeight: 600,
                color: 'var(--text-strong)',
                cursor: 'pointer',
              }}
            >
              Reset to default layout
            </button>
          </div>
        )}
      </div>

      {visibleIds.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 32, color: 'var(--text-muted)', fontSize: 14 }}>
          All widgets are hidden — use Customize to restore them.
        </div>
      ) : (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={visibleIds} strategy={rectSortingStrategy}>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 420px), 1fr))',
                gap: 20,
                alignItems: 'start',
              }}
            >
              {visibleIds.map((id) => (
                <WidgetShell
                  key={id}
                  id={id}
                  title={byId[id].title}
                  size={byId[id].size}
                  tall={byId[id].tall}
                  compact={byId[id].compact}
                  locked={byId[id].locked}
                  onHide={() => onHide(id)}
                >
                  {renderers[id] ? renderers[id]() : null}
                </WidgetShell>
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}
    </div>
  );
}
